import { Task } from '../types';
import { isPurePersonContact, isContactVorgang } from '../components/communication/personModel';

/**
 * Non-destructive TypeScript domain views over the persisted `Task` interface.
 *
 * IMPORTANT (Zero-Regression Guarantee):
 * These types do NOT change the runtime shape or Firestore serialization of `Task`.
 * Instead, they provide compile-time narrowing via type guards so callers know
 * whether they are dealing with a Standard Task, Milestone, Deadline, Person Contact,
 * or Contact-Vorgang.
 */

export interface StandardTaskView extends Task {
  type?: 'task' | 'meeting' | 'decision' | 'approval' | 'workshop' | 'iteration' | 'review' | 'cooperation';
}

export interface MilestoneTaskView extends Task {
  type: 'milestone';
}

export interface DeadlineTaskView extends Task {
  type: 'deadline';
}

export interface PersonContactView extends Task {
  type?: 'contact' | 'task';
}

export interface ContactVorgangView extends Task {
  category?: 'Kommunikation' | string;
  contactId?: string;
}

export interface IdeaTaskView extends Task {
  type: 'idea';
}

export interface FolderTaskView extends Task {
  type: 'folder' | 'topic';
}

export function isMilestoneTaskView(task: Task): task is MilestoneTaskView {
  return task.type === 'milestone';
}

export function isDeadlineTaskView(task: Task): task is DeadlineTaskView {
  return task.type === 'deadline';
}

export function isMilestoneOrDeadlineView(task: Task): task is MilestoneTaskView | DeadlineTaskView {
  return task.type === 'milestone' || task.type === 'deadline';
}

export function isIdeaTaskView(task: Task): task is IdeaTaskView {
  return task.type === 'idea';
}

export function isFolderTaskView(task: Task): task is FolderTaskView {
  return task.type === 'folder' || task.type === 'topic';
}

export function isPersonContactView(task: Task): task is PersonContactView {
  return task.type === 'contact' || isPurePersonContact(task);
}

export function isContactVorgangView(task: Task, isFromCommProject = false): task is ContactVorgangView {
  return isContactVorgang(task, isFromCommProject);
}

export function isDatedContactVorgangView(task: Task, isFromCommProject = false): task is ContactVorgangView {
  return isContactVorgangView(task, isFromCommProject) && hasTaskDate(task);
}

export function isStandardTaskView(task: Task): task is StandardTaskView {
  return (
    !isPersonContactView(task) &&
    !isMilestoneOrDeadlineView(task) &&
    !isIdeaTaskView(task) &&
    !isFolderTaskView(task)
  );
}

export function hasTaskDate(task: Pick<Task, 'start' | 'end'>): boolean {
  return Boolean(task.start?.trim() || task.end?.trim());
}
